import React, { useState } from 'react';
import { Edit3, Plus, X, Upload, Linkedin, ChevronDown, ExternalLink } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopHeaderBar from '../components/TopHeaderBar';
import { useAuth } from '../context/AuthContext';

const MERCOR_PURPLE = '#5B3DC5';

/* Tab Data */
const PROFILE_TABS = [
  { id: 'resume', label: 'Resume' },
  { id: 'location', label: 'Location & Work Authorization' },
  { id: 'availability', label: 'Availability' },
  { id: 'work-preferences', label: 'Work Preferences' },
  { id: 'communications', label: 'Communications' },
  { id: 'account', label: 'Account' },
];

/* Reusable Field Components - exact Mercor sizing */
const FieldLabel = ({ children, required = false }) => (
  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '6px' }}>
    {children}{required && <span style={{ color: '#EF4444', marginLeft: '2px' }}>*</span>}
  </label>
);

const FieldInput = ({ value, onChange, placeholder = '', type = 'text', disabled = false, className = '', style: extraStyle = {} }) => (
  <input
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    disabled={disabled}
    className={`w-full focus:outline-none focus:border-gray-400 transition-colors ${className}`}
    style={{
      padding: '10px 14px',
      fontSize: '14px',
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      backgroundColor: disabled ? '#F9FAFB' : '#FFFFFF',
      color: disabled ? '#9CA3AF' : '#1A1A1A',
      ...extraStyle,
    }}
  />
);

const FieldTextarea = ({ value, onChange, placeholder = '', rows = 4 }) => (
  <textarea
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    rows={rows}
    className="w-full focus:outline-none focus:border-gray-400 transition-colors resize-none"
    style={{
      padding: '10px 14px',
      fontSize: '14px',
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      backgroundColor: '#FFFFFF',
      color: '#1A1A1A',
    }}
  />
);

const SectionTitle = ({ title, onEdit }) => (
  <div className="flex items-center justify-between" style={{ marginBottom: '16px' }}>
    <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A' }}>{title}</h3>
    {onEdit && (
      <button onClick={onEdit} className="hover:bg-gray-100 rounded-lg" style={{ padding: '6px', color: '#9CA3AF' }}>
        <Edit3 style={{ width: '16px', height: '16px' }} />
      </button>
    )}
  </div>
);

const AddButton = ({ label, onClick }) => (
  <button onClick={onClick} className="flex items-center hover:opacity-80" style={{ gap: '6px', fontSize: '14px', fontWeight: 500, color: MERCOR_PURPLE, marginTop: '12px' }}>
    <Plus style={{ width: '16px', height: '16px' }} /> {label}
  </button>
);

/* Toggle Switch */
const ToggleSwitch = ({ enabled, onChange }) => (
  <button onClick={() => onChange(!enabled)} className="relative shrink-0 cursor-pointer transition-colors duration-200"
    style={{ width: '44px', height: '24px', borderRadius: '12px', backgroundColor: enabled ? MERCOR_PURPLE : '#D1D5DB' }}>
    <div className="absolute flex items-center justify-center rounded-full bg-white shadow-sm transition-transform duration-200"
      style={{ width: '20px', height: '20px', top: '2px', transform: enabled ? 'translateX(22px)' : 'translateX(2px)' }}>
      {enabled && <svg style={{ width: '12px', height: '12px' }} viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke={MERCOR_PURPLE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
    </div>
  </button>
);

/* Main Profile Page */
const MercorProfilePage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('resume');
  const userName = user?.name || 'User Name';
  const initials = userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  // Resume state
  const [fullName, setFullName] = useState(userName);
  const [email, setEmail] = useState(user?.email || 'user@email.com');
  const [phone, setPhone] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [summary, setSummary] = useState('');
  const [skills, setSkills] = useState(['JavaScript', 'React', 'Python', 'Node.js']);
  const [newSkill, setNewSkill] = useState('');
  const [experiences, setExperiences] = useState([
    { company: 'Tech Company', role: 'Software Engineer', startYear: '2022', endYear: '2025', city: '', country: '', description: '' },
  ]);
  const [educations, setEducations] = useState([
    { school: 'University Name', degree: 'Bachelor of Technology', startYear: '2018', endYear: '2022', major: 'Computer Science', gpa: '' },
  ]);
  const [projects, setProjects] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [awards, setAwards] = useState([]);

  // Location state
  const [country, setCountry] = useState('');
  const [stateRegion, setStateRegion] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [dob, setDob] = useState('');
  const [workAuthConfirm1, setWorkAuthConfirm1] = useState(false);
  const [workAuthConfirm2, setWorkAuthConfirm2] = useState(false);

  // Work Prefs
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [fullTimePay, setFullTimePay] = useState('0');
  const [partTimePay, setPartTimePay] = useState('0');

  // Communications
  const [emailNotifs, setEmailNotifs] = useState(true);
  const [smsNotifs, setSmsNotifs] = useState(false);
  const [marketingEmails, setMarketingEmails] = useState(true);

  // Account
  const [generativePhoto, setGenerativePhoto] = useState(false);

  const addSkill = () => {
    if (newSkill.trim() && !skills.includes(newSkill.trim())) {
      setSkills([...skills, newSkill.trim()]);
      setNewSkill('');
    }
  };
  const removeSkill = (skill) => setSkills(skills.filter(s => s !== skill));

  const DOMAINS = [
    'Software Engineering', 'Other Engineering', 'Medicine', 'Law', 'Data Analysis',
    'Finance', 'Business Operations', 'Life, Physical, and Social Science',
    'Arts & Design', 'Language and Audio', 'Humanities', 'Miscellaneous',
  ];

  const HOBBIES = ['Football', 'Basketball', 'Soccer', 'Cricket', 'Volleyball', 'Hockey', 'Golf', 'Shopping/Fashion'];

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#FFFFFF' }}>
      <Sidebar />
      <div className="flex-1 flex flex-col" style={{ marginLeft: '0px', paddingTop: '56px', paddingBottom: '56px' }}>
        <style>{`@media(min-width:768px){.profile-main{margin-left:60px!important;padding-top:0!important;padding-bottom:0!important}}`}</style>
        <div className="profile-main flex-1 flex flex-col" style={{ marginLeft: '0px', paddingTop: '56px', paddingBottom: '56px' }}>
          <TopHeaderBar />
          <div className="flex-1 overflow-y-auto">
            <div style={{ maxWidth: '960px', margin: '0 auto', padding: '32px 24px' }}>

              {/* Title */}
              <h1 style={{ fontSize: '26px', fontWeight: 700, color: '#1A1A1A', marginBottom: '12px' }}>Profile</h1>

              {/* Tabs */}
              <div className="overflow-x-auto scrollbar-hide" style={{ borderBottom: '1px solid #E5E7EB', marginBottom: '32px' }}>
                <div className="flex items-center" style={{ gap: '0px' }}>
                  {PROFILE_TABS.map(tab => {
                    const isActive = activeTab === tab.id;
                    return (
                      <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                        className="whitespace-nowrap shrink-0"
                        style={{
                          padding: '10px 16px',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: isActive ? '#1A1A1A' : '#6B7280',
                          borderBottom: isActive ? `2px solid ${MERCOR_PURPLE}` : '2px solid transparent',
                          marginBottom: '-1px',
                        }}>
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ═══ RESUME TAB ═══ */}
              {activeTab === 'resume' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
                  {/* Avatar + Name */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center" style={{ gap: '16px' }}>
                    <div className="flex items-center justify-center shrink-0 text-white"
                      style={{ width: '56px', height: '56px', borderRadius: '50%', fontSize: '18px', fontWeight: 700, backgroundColor: '#7C3AED' }}>
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p style={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A' }}>{fullName}</p>
                      <p style={{ fontSize: '14px', color: '#6B7280' }}>{email}</p>
                    </div>
                    <button className="flex items-center hover:bg-gray-50"
                      style={{ gap: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 500, color: '#374151', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                      <Edit3 style={{ width: '14px', height: '14px' }} /> Edit Profile
                    </button>
                  </div>

                  {/* Basic Info */}
                  <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', padding: '24px' }}>
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '20px' }}>This will be shown to companies to find you opportunities</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: '16px', marginBottom: '16px' }}>
                      <div><FieldLabel required>Full Name</FieldLabel><FieldInput value={fullName} onChange={e => setFullName(e.target.value)} /></div>
                      <div><FieldLabel required>Email</FieldLabel><FieldInput value={email} onChange={e => setEmail(e.target.value)} type="email" /></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: '16px', marginBottom: '16px' }}>
                      <div>
                        <FieldLabel>Phone</FieldLabel>
                        <div className="flex">
                          <div className="flex items-center shrink-0"
                            style={{ padding: '10px 10px', fontSize: '14px', border: '1px solid #E5E7EB', borderRight: 'none', borderRadius: '8px 0 0 8px', backgroundColor: '#F9FAFB', color: '#6B7280', gap: '4px' }}>
                            <span>🇺🇸</span> +1
                            <ChevronDown style={{ width: '12px', height: '12px', color: '#9CA3AF' }} />
                          </div>
                          <FieldInput value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone number" style={{ borderRadius: '0 8px 8px 0' }} />
                        </div>
                      </div>
                      <div>
                        <FieldLabel>LinkedIn URL</FieldLabel>
                        <div className="flex" style={{ gap: '8px' }}>
                          <FieldInput value={linkedinUrl} onChange={e => setLinkedinUrl(e.target.value)} placeholder="https://linkedin.com/in/..." />
                          <button className="flex items-center shrink-0 text-white hover:opacity-90"
                            style={{ padding: '10px 14px', fontSize: '13px', fontWeight: 600, backgroundColor: '#0A66C2', borderRadius: '8px', gap: '6px' }}>
                            <Linkedin style={{ width: '14px', height: '14px' }} /> Link
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Resume Upload */}
                    <div style={{ marginBottom: '16px' }}>
                      <FieldLabel required>Resume</FieldLabel>
                      <div className="cursor-pointer hover:border-gray-300 transition-colors"
                        style={{ border: '2px dashed #E5E7EB', borderRadius: '10px', padding: '32px', textAlign: 'center' }}>
                        <div className="flex items-center justify-center mx-auto"
                          style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#EDE9FE', marginBottom: '12px' }}>
                          <Upload style={{ width: '20px', height: '20px', color: MERCOR_PURPLE }} />
                        </div>
                        <p style={{ fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>Drop your resume here or click to upload</p>
                        <p style={{ fontSize: '12px', color: '#9CA3AF' }}>PDF, DOC, DOCX (Max 5MB)</p>
                      </div>
                    </div>

                    {/* Summary */}
                    <div>
                      <FieldLabel>Summary</FieldLabel>
                      <FieldTextarea value={summary} onChange={e => setSummary(e.target.value)} placeholder="Profile Summary — describe your experience, skills, and what you're looking for..." rows={4} />
                    </div>
                  </div>

                  {/* Work Experience */}
                  <div>
                    <SectionTitle title="Work Experience" onEdit={() => {}} />
                    {experiences.map((exp, i) => (
                      <div key={i} className="relative" style={{ border: '1px solid #E5E7EB', borderRadius: '10px', padding: '24px', marginBottom: '12px' }}>
                        <button onClick={() => setExperiences(experiences.filter((_, idx) => idx !== i))}
                          className="absolute hover:text-red-500" style={{ top: '16px', right: '16px', padding: '4px', color: '#9CA3AF' }}>
                          <X style={{ width: '16px', height: '16px' }} />
                        </button>
                        <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: '16px', marginBottom: '16px' }}>
                          <div><FieldLabel>Company</FieldLabel><FieldInput value={exp.company} onChange={e => { const n = [...experiences]; n[i].company = e.target.value; setExperiences(n); }} /></div>
                          <div><FieldLabel>Role</FieldLabel><FieldInput value={exp.role} onChange={e => { const n = [...experiences]; n[i].role = e.target.value; setExperiences(n); }} /></div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4" style={{ gap: '16px', marginBottom: '16px' }}>
                          <div><FieldLabel>Start year</FieldLabel><FieldInput value={exp.startYear} onChange={e => { const n = [...experiences]; n[i].startYear = e.target.value; setExperiences(n); }} placeholder="2020" /></div>
                          <div><FieldLabel>End year</FieldLabel><FieldInput value={exp.endYear} onChange={e => { const n = [...experiences]; n[i].endYear = e.target.value; setExperiences(n); }} placeholder="Present" /></div>
                          <div><FieldLabel>City</FieldLabel><FieldInput value={exp.city} onChange={e => { const n = [...experiences]; n[i].city = e.target.value; setExperiences(n); }} placeholder="San Francisco" /></div>
                          <div><FieldLabel>Country</FieldLabel><FieldInput value={exp.country} onChange={e => { const n = [...experiences]; n[i].country = e.target.value; setExperiences(n); }} placeholder="United States" /></div>
                        </div>
                        <div><FieldLabel>Description</FieldLabel><FieldTextarea value={exp.description} onChange={e => { const n = [...experiences]; n[i].description = e.target.value; setExperiences(n); }} placeholder="Describe your role and achievements..." /></div>
                      </div>
                    ))}
                    <AddButton label="Add Work Experience" onClick={() => setExperiences([...experiences, { company: '', role: '', startYear: '', endYear: '', city: '', country: '', description: '' }])} />
                  </div>

                  {/* Education */}
                  <div>
                    <SectionTitle title="Education" onEdit={() => {}} />
                    {educations.map((edu, i) => (
                      <div key={i} className="relative" style={{ border: '1px solid #E5E7EB', borderRadius: '10px', padding: '24px', marginBottom: '12px' }}>
                        <button onClick={() => setEducations(educations.filter((_, idx) => idx !== i))}
                          className="absolute hover:text-red-500" style={{ top: '16px', right: '16px', padding: '4px', color: '#9CA3AF' }}>
                          <X style={{ width: '16px', height: '16px' }} />
                        </button>
                        <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: '16px', marginBottom: '16px' }}>
                          <div><FieldLabel>School</FieldLabel><FieldInput value={edu.school} onChange={e => { const n = [...educations]; n[i].school = e.target.value; setEducations(n); }} /></div>
                          <div><FieldLabel>Degree</FieldLabel><FieldInput value={edu.degree} onChange={e => { const n = [...educations]; n[i].degree = e.target.value; setEducations(n); }} /></div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4" style={{ gap: '16px' }}>
                          <div><FieldLabel>Start year</FieldLabel><FieldInput value={edu.startYear} onChange={e => { const n = [...educations]; n[i].startYear = e.target.value; setEducations(n); }} /></div>
                          <div><FieldLabel>End year</FieldLabel><FieldInput value={edu.endYear} onChange={e => { const n = [...educations]; n[i].endYear = e.target.value; setEducations(n); }} /></div>
                          <div><FieldLabel>Major</FieldLabel><FieldInput value={edu.major} onChange={e => { const n = [...educations]; n[i].major = e.target.value; setEducations(n); }} /></div>
                          <div><FieldLabel>GPA</FieldLabel><FieldInput value={edu.gpa} onChange={e => { const n = [...educations]; n[i].gpa = e.target.value; setEducations(n); }} placeholder="3.8" /></div>
                        </div>
                      </div>
                    ))}
                    <AddButton label="Add Education" onClick={() => setEducations([...educations, { school: '', degree: '', startYear: '', endYear: '', major: '', gpa: '' }])} />
                  </div>

                  {/* Projects */}
                  <div>
                    <SectionTitle title="Projects" onEdit={() => {}} />
                    {projects.length === 0 && <p style={{ fontSize: '13px', color: '#9CA3AF' }}>No projects added yet.</p>}
                    <AddButton label="Add Project" onClick={() => setProjects([...projects, { name: '', startYear: '', endYear: '', description: '' }])} />
                  </div>

                  {/* Certifications */}
                  <div>
                    <SectionTitle title="Certifications" onEdit={() => {}} />
                    {certifications.length === 0 && <p style={{ fontSize: '13px', color: '#9CA3AF' }}>No certifications added yet.</p>}
                    <AddButton label="Add Certification" onClick={() => setCertifications([...certifications, ''])} />
                  </div>

                  {/* Awards */}
                  <div>
                    <SectionTitle title="Awards" onEdit={() => {}} />
                    {awards.length === 0 && <p style={{ fontSize: '13px', color: '#9CA3AF' }}>No awards added yet.</p>}
                    <AddButton label="Add Award" onClick={() => setAwards([...awards, ''])} />
                  </div>

                  {/* Profiles */}
                  <div>
                    <SectionTitle title="Profiles" onEdit={() => {}} />
                    <div className="grid grid-cols-2" style={{ gap: '16px' }}>
                      <div><FieldLabel>LeetCode</FieldLabel><FieldInput placeholder="leetcode-username" /></div>
                      <div><FieldLabel>GitHub</FieldLabel><FieldInput placeholder="github-username" /></div>
                    </div>
                  </div>

                  {/* Links */}
                  <div>
                    <SectionTitle title="Links" onEdit={() => {}} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div><FieldLabel>Portfolio URL</FieldLabel><FieldInput placeholder="https://yourportfolio.com" /></div>
                      <div><FieldLabel>Other links</FieldLabel><FieldInput placeholder="https://..." /></div>
                    </div>
                    <AddButton label="Add more links" onClick={() => {}} />
                  </div>

                  {/* Skills */}
                  <div>
                    <SectionTitle title="Skills" onEdit={() => {}} />
                    <div className="flex flex-wrap" style={{ gap: '8px', marginBottom: '12px' }}>
                      {skills.map(skill => (
                        <span key={skill} className="inline-flex items-center"
                          style={{ gap: '4px', padding: '6px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #E5E7EB', backgroundColor: '#F9FAFB', color: '#1A1A1A' }}>
                          {skill}
                          <button onClick={() => removeSkill(skill)} className="hover:text-red-500" style={{ color: '#9CA3AF' }}><X style={{ width: '12px', height: '12px' }} /></button>
                        </span>
                      ))}
                    </div>
                    <div className="flex" style={{ gap: '8px' }}>
                      <FieldInput value={newSkill} onChange={e => setNewSkill(e.target.value)} placeholder="Add a skill..." style={{ maxWidth: '240px' }} />
                      <button onClick={addSkill} className="text-white font-medium hover:opacity-90"
                        style={{ padding: '10px 16px', fontSize: '13px', backgroundColor: MERCOR_PURPLE, borderRadius: '8px' }}>Add</button>
                    </div>
                  </div>

                  {/* Languages */}
                  <div>
                    <SectionTitle title="Languages" onEdit={() => {}} />
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px' }}>What languages can you natively speak, read, and write?</p>
                    <FieldInput placeholder="Search and select languages..." />
                  </div>

                  {/* Hobbies */}
                  <div>
                    <SectionTitle title="Hobbies" onEdit={() => {}} />
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px' }}>Select all that apply:</p>
                    <div className="flex flex-wrap" style={{ gap: '8px' }}>
                      {HOBBIES.map(hobby => (
                        <button key={hobby} className="hover:border-gray-300"
                          style={{ padding: '8px 14px', fontSize: '13px', border: '1px solid #E5E7EB', borderRadius: '8px', color: '#374151' }}>
                          {hobby}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ═══ LOCATION TAB ═══ */}
              {activeTab === 'location' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A', marginBottom: '4px' }}>Location of Residence</h3>
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '20px' }}>Where you're based for most of the year</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: '16px', marginBottom: '16px' }}>
                      <div><FieldLabel>Country</FieldLabel><FieldInput value={country} onChange={e => setCountry(e.target.value)} placeholder="Select country" /></div>
                      <div><FieldLabel>State / Province / Region</FieldLabel><FieldInput value={stateRegion} onChange={e => setStateRegion(e.target.value)} placeholder="Select state" /></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: '16px', marginBottom: '16px' }}>
                      <div><FieldLabel>City</FieldLabel><FieldInput value={city} onChange={e => setCity(e.target.value)} placeholder="City" /></div>
                      <div><FieldLabel>Postal Code</FieldLabel><FieldInput value={postalCode} onChange={e => setPostalCode(e.target.value)} placeholder="Postal code" /></div>
                    </div>
                    <label className="flex items-center cursor-pointer" style={{ gap: '8px' }}>
                      <input type="checkbox" style={{ accentColor: MERCOR_PURPLE }} defaultChecked />
                      <span style={{ fontSize: '13px', color: '#4B5563' }}>My location of residence and physical working location are the same.</span>
                    </label>
                  </div>
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A', marginBottom: '4px' }}>Work Authorization</h3>
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '8px' }}>Confirm your legally authorized work status</p>
                    <p style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '20px' }}>Last verified: 02/21/2026</p>
                    <div style={{ marginBottom: '20px' }}>
                      <FieldLabel>Date of Birth (MM/DD/YYYY)</FieldLabel>
                      <FieldInput value={dob} onChange={e => setDob(e.target.value)} placeholder="MM/DD/YYYY" style={{ maxWidth: '240px' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <label className="flex items-start cursor-pointer" style={{ gap: '8px' }}>
                        <input type="checkbox" checked={workAuthConfirm1} onChange={e => setWorkAuthConfirm1(e.target.checked)} style={{ accentColor: MERCOR_PURPLE, marginTop: '2px' }} />
                        <span style={{ fontSize: '13px', color: '#4B5563' }}>I confirm that I am legally authorized to work from my country of residence.*</span>
                      </label>
                      <label className="flex items-start cursor-pointer" style={{ gap: '8px' }}>
                        <input type="checkbox" checked={workAuthConfirm2} onChange={e => setWorkAuthConfirm2(e.target.checked)} style={{ accentColor: MERCOR_PURPLE, marginTop: '2px' }} />
                        <span style={{ fontSize: '13px', color: '#4B5563' }}>I agree to remain working from my country, and to notify Mercor in writing prior to any change.*</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* ═══ AVAILABILITY TAB ═══ */}
              {activeTab === 'availability' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A', marginBottom: '4px' }}>Availability</h3>
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px' }}>Tell us when you're available to work</p>
                  </div>
                  <div className="grid grid-cols-2" style={{ gap: '16px' }}>
                    <div><FieldLabel>Hours per week</FieldLabel><FieldInput placeholder="40" /></div>
                    <div><FieldLabel>Earliest start date</FieldLabel><FieldInput placeholder="Immediately" /></div>
                  </div>
                  <div>
                    <FieldLabel>Working hours</FieldLabel>
                    <div className="flex items-center" style={{ gap: '12px' }}>
                      <FieldInput placeholder="9:00 AM" style={{ maxWidth: '150px' }} />
                      <span style={{ fontSize: '13px', color: '#9CA3AF' }}>to</span>
                      <FieldInput placeholder="5:00 PM" style={{ maxWidth: '150px' }} />
                    </div>
                  </div>
                  <div><FieldLabel>Timezone</FieldLabel><FieldInput placeholder="UTC-8 (Pacific Time)" /></div>
                </div>
              )}

              {/* ═══ WORK PREFERENCES TAB ═══ */}
              {activeTab === 'work-preferences' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A', marginBottom: '4px' }}>Domain Interests</h3>
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px' }}>What domains are you interested in? Select all that apply:</p>
                    <div className="flex flex-wrap" style={{ gap: '8px' }}>
                      {DOMAINS.map(domain => {
                        const selected = selectedDomains.includes(domain);
                        return (
                          <button key={domain} onClick={() => setSelectedDomains(selected ? selectedDomains.filter(d => d !== domain) : [...selectedDomains, domain])}
                            style={{
                              padding: '8px 14px', fontSize: '13px', borderRadius: '8px', transition: 'all 0.15s',
                              ...(selected ? { backgroundColor: '#F5F3FF', border: `1px solid ${MERCOR_PURPLE}`, color: MERCOR_PURPLE, fontWeight: 500 }
                                : { border: '1px solid #E5E7EB', color: '#4B5563' }),
                            }}>
                            {domain}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A', marginBottom: '4px' }}>Minimum expected compensation</h3>
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '20px' }}>We won't reach out about roles below this. This stays private.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: '16px' }}>
                      <div>
                        <FieldLabel>Full-time</FieldLabel>
                        <div className="flex items-center" style={{ gap: '8px' }}>
                          <span style={{ fontSize: '14px', color: '#6B7280' }}>$</span>
                          <FieldInput value={fullTimePay} onChange={e => setFullTimePay(e.target.value)} style={{ maxWidth: '120px' }} />
                          <span style={{ fontSize: '14px', color: '#6B7280' }}>/ year</span>
                        </div>
                      </div>
                      <div>
                        <FieldLabel>Part-time</FieldLabel>
                        <div className="flex items-center" style={{ gap: '8px' }}>
                          <span style={{ fontSize: '14px', color: '#6B7280' }}>$</span>
                          <FieldInput value={partTimePay} onChange={e => setPartTimePay(e.target.value)} style={{ maxWidth: '120px' }} />
                          <span style={{ fontSize: '14px', color: '#6B7280' }}>/ hour</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ═══ COMMUNICATIONS TAB ═══ */}
              {activeTab === 'communications' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A', marginBottom: '4px' }}>Communication Preferences</h3>
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px' }}>Choose how you'd like to be contacted</p>
                  </div>
                  {[
                    { label: 'Email notifications', desc: 'Get notified about new opportunities, updates, and important announcements via email', enabled: emailNotifs, onChange: setEmailNotifs },
                    { label: 'SMS notifications', desc: 'Receive text message alerts for urgent updates and interview reminders', enabled: smsNotifs, onChange: setSmsNotifs },
                    { label: 'Marketing emails', desc: 'Stay informed about Mercor news, tips, and feature updates', enabled: marketingEmails, onChange: setMarketingEmails },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start justify-between" style={{ padding: '20px', border: '1px solid #E5E7EB', borderRadius: '10px' }}>
                      <div className="flex-1" style={{ marginRight: '16px' }}>
                        <p style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', marginBottom: '4px' }}>{item.label}</p>
                        <p style={{ fontSize: '13px', color: '#6B7280' }}>{item.desc}</p>
                      </div>
                      <ToggleSwitch enabled={item.enabled} onChange={item.onChange} />
                    </div>
                  ))}
                </div>
              )}

              {/* ═══ ACCOUNT TAB ═══ */}
              {activeTab === 'account' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A', marginBottom: '16px' }}>Account</h3>
                    <div className="flex items-center" style={{ gap: '20px', marginBottom: '24px' }}>
                      <div className="flex items-center justify-center text-white"
                        style={{ width: '72px', height: '72px', borderRadius: '50%', fontSize: '22px', fontWeight: 700, backgroundColor: '#A78BFA' }}>{initials}</div>
                      <div>
                        <button className="hover:bg-gray-50" style={{ padding: '8px 16px', fontSize: '13px', fontWeight: 500, color: '#374151', border: '1px solid #E5E7EB', borderRadius: '8px', marginBottom: '4px' }}>Change avatar</button>
                        <p style={{ fontSize: '12px', color: '#9CA3AF' }}>JPG, PNG, GIF. Max 2 MB.</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start justify-between" style={{ padding: '20px', border: '1px solid #E5E7EB', borderRadius: '10px' }}>
                    <div className="flex-1" style={{ marginRight: '16px' }}>
                      <p style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', marginBottom: '4px' }}>Generative Profile Pictures</p>
                      <p style={{ fontSize: '13px', color: '#6B7280' }}>Let Mercor generate a professional photo from your AI interview.</p>
                    </div>
                    <ToggleSwitch enabled={generativePhoto} onChange={setGenerativePhoto} />
                  </div>

                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A', marginBottom: '8px' }}>Payout Preferences</h3>
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px' }}>Choose how you want to receive your payouts.</p>
                    <label className="flex items-start cursor-pointer hover:bg-gray-50"
                      style={{ gap: '12px', padding: '16px', border: '1px solid #E5E7EB', borderRadius: '10px' }}>
                      <input type="radio" name="payout" defaultChecked style={{ accentColor: MERCOR_PURPLE, marginTop: '2px' }} />
                      <div>
                        <p style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A' }}>Standard Payout</p>
                        <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>Funds arrive in your Stripe account, then transfer to your bank within 5 business days</p>
                        <span style={{ display: 'inline-block', marginTop: '6px', padding: '2px 8px', backgroundColor: '#F3F4F6', fontSize: '11px', color: '#6B7280', borderRadius: '4px' }}>Free · Up to 5 business days</span>
                      </div>
                    </label>
                  </div>

                  <div className="flex items-start justify-between" style={{ padding: '20px', border: '1px solid #E5E7EB', borderRadius: '10px' }}>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', marginBottom: '4px' }}>Change Email</p>
                      <p style={{ fontSize: '13px', color: '#6B7280' }}>Transfer all your data to a new email address.</p>
                    </div>
                    <button className="shrink-0 hover:bg-gray-50" style={{ padding: '8px 16px', fontSize: '13px', fontWeight: 500, color: '#374151', border: '1px solid #E5E7EB', borderRadius: '8px' }}>Change email</button>
                  </div>

                  <div className="flex items-start justify-between" style={{ padding: '20px', border: '1px solid #FCA5A5', borderRadius: '10px', backgroundColor: '#FEF2F2' }}>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: 600, color: '#B91C1C', marginBottom: '4px' }}>Delete Account</p>
                      <p style={{ fontSize: '13px', color: '#EF4444' }}>Permanently delete the account and all data from the platform.</p>
                    </div>
                    <button className="shrink-0 text-white hover:opacity-90" style={{ padding: '8px 16px', fontSize: '13px', fontWeight: 600, backgroundColor: '#DC2626', borderRadius: '8px' }}>Delete account</button>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MercorProfilePage;
